import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Card, CardBody, CardImg, CardTitle } from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "assets/scss/custom.scss";
import AddToCart from "./AddToCart";
import { useHolidayGuard } from "hooks/useHolidayGuard";

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

const Offers = ({ offers }) => {
  const [showAddToCartModal, setAddToCartModal] = useState(false);
  const [offer, setOffer] = useState({});
  const width = useWindowWidth();
  const { restaurantsDetail } = useSelector((state) => state.restaurant);
  const bgColor = restaurantsDetail?.adminColor;

  const shouldShowPagination =
    (width < 576 && offers.length > 1) ||
    (width >= 576 && width < 768 && offers.length > 2) ||
    (width >= 768 && width < 1200 && offers.length > 2) ||
    (width >= 1200 && offers.length > 3);

  const addToCartModalToggler = () => {
    if (showAddToCartModal) {
      setOffer({});
    }
    setAddToCartModal(!showAddToCartModal);
  };

  const { checkAndToast } = useHolidayGuard(restaurantsDetail, "dinein");
  const handleAddtoCartBtn = async (offer) => {
    if (checkAndToast()) return;

    setOffer(offer);
    addToCartModalToggler();
  };

    useEffect(() => {
    const updateBulletColor = () => {
      const activeBullet = document.querySelector(
        ".swiper-pagination-bullet-active"
      );
      if (activeBullet) {
        activeBullet.style.backgroundColor = bgColor;
      }
    };

    updateBulletColor();
    const observer = new MutationObserver(updateBulletColor);
    const pagination = document.querySelector(".swiper-pagination");
    if (pagination) {
      observer.observe(pagination, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [bgColor, offers]);

  return (
    <div
      className="d-flex flex-column justify-content-center pt-0 pb-3"
      style={{ position: "relative" }}
    >
      <h1 className="text-center text-dark">Offers</h1>
      <div className="w-100 pt-3">
        <Swiper
          modules={[Pagination, A11y]}
          pagination={
            shouldShowPagination
              ? {
                  clickable: true,
                  el: ".swiper-pagination",
                  bulletClass: "swiper-pagination-bullet",
                  bulletActiveClass: "swiper-pagination-bullet-active",
                }
              : false
          }
          grabCursor={true}
          speed={600}
          slidesPerView={1.2}
          spaceBetween={12}
          breakpoints={{
            576: {
              slidesPerView: "auto",
              spaceBetween: 16,
            },
            768: {
              slidesPerView: "auto",
              spaceBetween: 20,
            },
            1200: {
              slidesPerView: "auto",
              spaceBetween: 24,
            },
          }}
        >
          {offers?.length > 0 &&
            offers.map((offer, index) => (
              <SwiperSlide
                key={index}
                style={{
                  width: "340px",
                  maxWidth: "340px",
                  height: "100%",
                  paddingLeft: "15px",
                  paddingRight: "15px",
                }}
                className="d-flex justify-content-center"
              >
                <Card
                  className="shadow-lg"
                  style={{
                    borderRadius: "1rem",
                    maxWidth: "340px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "480px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <CardImg
                    top
                    src={offer?.image}
                    alt={offer?.title}
                    style={{
                      height: "180px",
                      objectFit: "cover",
                      borderTopLeftRadius: "1rem",
                      borderTopRightRadius: "1rem",
                      borderBottom: "1px solid #eee",
                    }}
                  />
                  <CardBody
                    className="d-flex flex-column"
                    style={{
                      padding: "1rem",
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div>
                      <CardTitle
                        tag="h5"
                        className="text-dark fw-bold text-capitalize mb-3"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          borderBottom: "1px solid #eee",
                          textOverflow: "ellipsis",
                          fontSize: "1.1rem",
                          minHeight: "3.3em",
                          lineHeight: "1.1rem",
                        }}
                      >
                        {offer?.title}
                      </CardTitle>

                      <div
                        style={{
                          maxHeight: "100px",
                          minHeight: "100px",
                          overflowY: "auto",
                          marginBottom: "1rem",
                          paddingRight: "0.5rem",
                          paddingTop: "0px",
                        }}
                      >
                        {offer?.items?.map((item, idx, array) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center mb-2"
                            style={{
                              borderBottom:
                                idx < array.length - 1
                                  ? "1px dashed #e0e0e0"
                                  : "none",
                              paddingBottom: "6px",
                              minHeight: "60px",
                            }}
                          >
                            <img
                              src={item?.image}
                              alt={item?.itemName}
                              style={{
                                width: "45px",
                                height: "45px",
                                objectFit: "cover",
                                borderRadius: "0.5rem",
                                marginRight: "10px",
                                boxShadow: "0 0 4px rgba(0,0,0,0.05)",
                              }}
                            />
                            <div className="flex-grow-1">
                              <div className="fw-semibold text-dark text-truncate">
                                {item?.itemName}
                              </div>
                              <div className="small text-muted">
                                {item?.variant && `Variant: ${item.variant}`}
                              </div>
                              <div className="small text-muted">
                                {item?.quantity &&
                                  `Quantity: x${item.quantity}`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: "auto",
                        borderTop: "1px solid #eee",
                        paddingTop: "0.75rem",
                      }}
                    >
                      <div className="mb-2 fw-bold text-dark">
                        Price:{" "}
                        <span style={{ color: "#333" }}>
                          ${Number(offer?.price)}
                        </span>
                      </div>
                      <Button
                        style={{
                          background: bgColor,
                          color: "#fff",
                          borderRadius: "0.5rem",
                          padding: "0.5rem",
                          fontWeight: 500,
                          fontSize: "0.95rem",
                        }}
                        className="w-100 border-0"
                        onClick={() => handleAddtoCartBtn(offer, offer?.id)}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </SwiperSlide>
            ))}
        </Swiper>
        {shouldShowPagination && (
          <div
            className="swiper-pagination"
            style={{ position: "relative", marginTop: "20px" }}
          ></div>
        )}
      </div>
      <AddToCart
        isOpen={showAddToCartModal}
        toggler={addToCartModalToggler}
        showItem={offer}
        type="offer"
      />
    </div>
  );
};

export default Offers;
